import { useMemo, useState } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { IntegrationsSearchCard } from "./integrations-search-card";
import { IntegrationsSearchModal } from "./integrations-search-modal";

import type { CategoryCount, PipeItem } from "./integrations-search-types";

// ============================================================================
// Colors
// ============================================================================

const TEXT_PRIMARY = wacToken("color.extended.blue1000"); // N900
const TEXT_SECONDARY = wacToken("color.neutral.light800"); // N600
const TEXT_LINK = wacToken("color.primary.blue"); // B700
const BORDER_COLOR = wacToken("color.neutral.light300"); // N40

// ============================================================================
// Styles
// ============================================================================

const layoutStyle = css({
    display: "flex",
    gap: token("space.400"),
    [media.below.md]: { flexDirection: "column" }
});

const sidebarStyle = css({
    width: "240px",
    flexShrink: 0,
    [media.below.md]: { display: "none" }
});

const mainStyle = css({
    flex: 1,
    minWidth: 0
});

const mobileFiltersStyle = css({
    display: "none",
    [media.below.md]: { display: "block", marginBlockEnd: token("space.200") }
});

// Sidebar heading — "Discover" in black, slightly larger
const sidebarHeadingStyle = css({
    margin: `0 0 ${token("space.200")}`,
    fontSize: 16,
    fontWeight: 600,
    color: TEXT_PRIMARY,
    display: "flex",
    alignItems: "center",
    gap: token("space.100"),
    textTransform: "uppercase"
});

const resetButtonStyle = css({
    "background": "none",
    "border": "none",
    "padding": 0,
    "cursor": "pointer",
    "fontSize": 14,
    "color": TEXT_LINK,
    ":hover": { textDecoration: "underline" }
});

const categoryListStyle = css({
    listStyle: "none",
    margin: 0,
    padding: 0
});

const categoryItemStyle = css({
    marginBlockEnd: "4px"
});

// Category buttons — black text, NO background on hover, blue text on hover
const categoryButtonBaseStyle = css({
    "width": "100%",
    "textAlign": "left",
    "background": "none",
    "border": "none",
    "borderRadius": "4px",
    "padding": `${token("space.100")} ${token("space.150")}`,
    "cursor": "pointer",
    "fontSize": 14,
    "fontFamily": "Charlie Text",
    "color": TEXT_PRIMARY,
    "transition": "color 0.15s ease",

    ":hover": {
        color: TEXT_LINK
    }
});

// Active category — blue text, bold, NO background
const categoryButtonActiveStyle = css({
    fontWeight: 600,
    color: TEXT_LINK
});

// Mobile select
const mobileSelectRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.150")
});

const mobileLabelStyle = css({
    fontSize: 16,
    fontWeight: 600,
    color: TEXT_PRIMARY
});

const mobileSelectStyle = css({
    flex: 1,
    padding: `${token("space.100")} ${token("space.150")}`,
    borderRadius: "4px",
    border: `1px solid ${BORDER_COLOR}`,
    fontSize: 14,
    fontFamily: "Charlie Text",
    backgroundColor: wacToken("color.neutral.light0"),
    color: TEXT_PRIMARY,
    cursor: "pointer"
});

// Breadcrumb — bigger, no bold on active category
const breadcrumbStyle = css({
    fontSize: 16,
    fontWeight: 500,
    color: TEXT_PRIMARY,
    margin: `0 0 ${token("space.200")}`
});

const breadcrumbActiveStyle = css({
    color: TEXT_PRIMARY,
    fontWeight: 500
});

// Results header
const resultsHeaderStyle = css({
    fontSize: 14,
    fontWeight: 600,
    color: TEXT_PRIMARY,
    textTransform: "uppercase",
    letterSpacing: "1px",
    margin: `0 0 ${token("space.250")}`
});

// Search
const searchWrapperStyle = css({
    position: "relative",
    marginBlockEnd: token("space.300")
});

const searchInputStyle = css({
    "width": "100%",
    "padding": "10px 40px 10px 16px",
    "borderRadius": "4px",
    "border": `1px solid ${BORDER_COLOR}`,
    "fontSize": 14,
    "fontFamily": "Charlie Text",
    "color": TEXT_PRIMARY,
    "backgroundColor": wacToken("color.neutral.light0"),
    "boxSizing": "border-box",
    "outline": "none",
    ":focus": { borderColor: TEXT_LINK },
    "::placeholder": { color: TEXT_SECONDARY }
});

const clearButtonStyle = css({
    "position": "absolute",
    "right": "12px",
    "top": "50%",
    "transform": "translateY(-50%)",
    "background": "none",
    "border": "none",
    "fontSize": 18,
    "cursor": "pointer",
    "color": TEXT_SECONDARY,
    "padding": "4px",
    "lineHeight": 1,
    ":hover": { color: TEXT_PRIMARY }
});

// Grid
const gridStyle = css({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: token("space.250")
});

// Empty state
const emptyStateStyle = css({
    textAlign: "center",
    paddingBlock: token("space.600"),
    fontSize: 16,
    color: TEXT_SECONDARY
});

// ============================================================================
// Helpers
// ============================================================================

const buildCategoryId = (label: string) =>
    label.toLowerCase().replace(/\s+/g, "");

// ============================================================================
// Component
// ============================================================================

export interface IntegrationsSearchGridProps {
    items: PipeItem[];
    categories: CategoryCount[];
}

export const IntegrationsSearchGrid = ({
    items,
    categories
}: IntegrationsSearchGridProps) => {
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(
        null
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [modalItem, setModalItem] = useState<PipeItem | null>(null);

    const activeCategoryLabel = useMemo(() => {
        if (!activeCategoryId) return "All categories";
        return (
            categories.find((c) => c.id === activeCategoryId)?.label ??
            "All categories"
        );
    }, [categories, activeCategoryId]);

    const filteredItems = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        return items.filter((item) => {
            const catMatch =
                !activeCategoryId ||
                buildCategoryId(item.category || "Other") === activeCategoryId;

            const searchMatch =
                !term ||
                item.name.toLowerCase().includes(term) ||
                item.description.toLowerCase().includes(term) ||
                item.tags.some((t) => t.toLowerCase().includes(term));

            return catMatch && searchMatch;
        });
    }, [items, activeCategoryId, searchTerm]);

    return (
        <>
            <div css={layoutStyle}>
                {/* ── Desktop sidebar ── */}
                <aside css={sidebarStyle}>
                    <h5 css={sidebarHeadingStyle}>
                        <span>Discover</span>
                        {activeCategoryId && (
                            <>
                                <span>|</span>
                                <button
                                    type="button"
                                    css={resetButtonStyle}
                                    onClick={() => setActiveCategoryId(null)}
                                >
                                    Reset
                                </button>
                            </>
                        )}
                    </h5>

                    <ul css={categoryListStyle}>
                        <li css={categoryItemStyle}>
                            <button
                                type="button"
                                css={[
                                    categoryButtonBaseStyle,
                                    activeCategoryId === null &&
                                        categoryButtonActiveStyle
                                ]}
                                onClick={() => setActiveCategoryId(null)}
                            >
                                All categories ({items.length})
                            </button>
                        </li>

                        {categories.map((cat) => (
                            <li key={cat.id} css={categoryItemStyle}>
                                <button
                                    type="button"
                                    css={[
                                        categoryButtonBaseStyle,
                                        activeCategoryId === cat.id &&
                                            categoryButtonActiveStyle
                                    ]}
                                    onClick={() => setActiveCategoryId(cat.id)}
                                >
                                    {cat.label} ({cat.count})
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* ── Main content ── */}
                <section css={mainStyle}>
                    {/* Mobile filters */}
                    <div css={mobileFiltersStyle}>
                        <div css={mobileSelectRowStyle}>
                            <span css={mobileLabelStyle}>Discover</span>
                            <select
                                css={mobileSelectStyle}
                                value={activeCategoryId ?? ""}
                                onChange={(e) =>
                                    setActiveCategoryId(e.target.value || null)
                                }
                            >
                                <option value="">
                                    All categories ({items.length})
                                </option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.label} ({cat.count})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Breadcrumb */}
                    <p css={breadcrumbStyle}>
                        <span>Integrations / </span>
                        <span css={breadcrumbActiveStyle}>
                            {activeCategoryLabel}
                        </span>
                    </p>

                    {/* Results count */}
                    <h5 css={resultsHeaderStyle}>
                        {filteredItems.length}{" "}
                        {filteredItems.length === 1 ? "Result" : "Results"}
                    </h5>

                    {/* Search */}
                    <div css={searchWrapperStyle}>
                        <input
                            type="text"
                            css={searchInputStyle}
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Search integrations"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                css={clearButtonStyle}
                                onClick={() => setSearchTerm("")}
                                aria-label="Clear search"
                            >
                                ×
                            </button>
                        )}
                    </div>

                    {/* Grid */}
                    {filteredItems.length > 0 ? (
                        <div css={gridStyle}>
                            {filteredItems.map((item) => (
                                <IntegrationsSearchCard
                                    key={item.repositoryPath}
                                    item={item}
                                    onClick={setModalItem}
                                />
                            ))}
                        </div>
                    ) : (
                        <div css={emptyStateStyle}>No Results</div>
                    )}
                </section>
            </div>

            {/* ── Detail Modal ── */}
            {modalItem && (
                <IntegrationsSearchModal
                    item={modalItem}
                    onClose={() => setModalItem(null)}
                />
            )}
        </>
    );
};
